import { Button } from '@/components/ui/button'
import Card from 'app/frontend/base/components/Card'
import Select from 'app/frontend/base/components/Select'
import { Check, MessageCircleMore } from 'lucide-react'
import type { MapPOI } from '../utils'
import {
  choiceTitles,
  houseIconUrl,
  plotImageUrl,
  statusClassName,
} from './housingUi'
import PlotOccupantLabel from './PlotOccupantLabel'
import type { DisplayOccupant } from './types'

type Props = {
  choices: DisplayOccupant[]
  mapId: number
  plots: MapPOI[]
  occupantsByPlot: Record<string, DisplayOccupant[]>
  shardIndex: number
  disabled: boolean
  mutationPending: boolean
  userHasApprovedPlot: boolean
  onRequest: (rankedChoice: number, plotId?: string) => void
  onWithdraw: (rankedChoice: number) => void
  onOpenPlot: (plot: MapPOI) => void
  onEditNote: (occupant: DisplayOccupant | null) => void
}

const PersonalPlotChoices = ({
  choices,
  mapId,
  plots,
  occupantsByPlot,
  shardIndex,
  disabled,
  mutationPending,
  userHasApprovedPlot,
  onRequest,
  onWithdraw,
  onOpenPlot,
  onEditNote,
}: Props) => {
  return (
    <div className='grid gap-3 md:grid-cols-3'>
      {[1, 2, 3].map((rankedChoice) => {
        const choice = choices.find(
          (occupant) => occupant.ranked_choice === rankedChoice,
        )
        const plot = plots.find(
          (poi) => poi.Name === choice?.plot_id.toString(),
        )
        const choiceObsolete =
          userHasApprovedPlot && choice?.status !== 'approved'

        return (
          <Card
            key={rankedChoice}
            className={`flex flex-col gap-2 p-3 pt-2 ${
              choiceObsolete ? 'opacity-50' : ''
            } ${
              choice?.status === 'approved'
                ? 'border-success-foreground'
                : choice?.status === 'rejected'
                ? 'border-destructive-foreground'
                : ''
            }`}
          >
            <div className='flex items-center justify-center gap-2 font-medium'>
              <img
                src={`https://data.wowaudit.com/img/housing/icon_house_choice_${rankedChoice}.png`}
                alt=''
                className='h-8 shrink-0'
              />
              <span className='text-base'>
                {choiceTitles[rankedChoice]} choice
              </span>
            </div>

            {plot && choice ? (
              <button
                type='button'
                className='relative overflow-hidden rounded-md border text-left'
                onClick={() => onOpenPlot(plot)}
              >
                {choice.shard_index !== shardIndex ? (
                  <div className='absolute inset-0 z-10 flex items-center justify-center bg-background/80 text-sm font-medium'>
                    Subdivision {choice.shard_index + 1}
                  </div>
                ) : null}
                <img
                  src={plotImageUrl(mapId, plot.Name)}
                  alt={`Plot ${plot.Name}`}
                  className='aspect-video w-full object-cover'
                />
                <Card className='absolute bottom-2 right-2 px-2 py-1 text-xs shadow'>
                  {choice.status === 'requested' ? (
                    <PlotOccupantLabel
                      occupants={(occupantsByPlot[plot.Name] ?? []).filter(
                        (occupant) => occupant.shard_index === shardIndex,
                      )}
                    />
                  ) : (
                    <span className={statusClassName[choice.status]}>
                      {choice.status}
                    </span>
                  )}
                </Card>
              </button>
            ) : (
              <div className='flex min-h-40 h-full bg-card-nested items-center justify-center rounded-md border border-dashed'>
                <img
                  src={houseIconUrl}
                  alt=''
                  className='h-12 opacity-50 grayscale'
                />
              </div>
            )}

            {choice?.status === 'approved' ? (
              <Button
                variant='outline'
                disabled={mutationPending}
                onClick={() => onWithdraw(rankedChoice)}
              >
                Withdraw (Plot {plot?.Name})
              </Button>
            ) : (
              <div className='flex gap-2'>
                <Select
                  value={plot?.Name}
                  setValue={(plotId) =>
                    plotId
                      ? onRequest(rankedChoice, plotId)
                      : onWithdraw(rankedChoice)
                  }
                  disabled={disabled || mutationPending}
                  isClearable={!!choice}
                  options={plots.map((plotOption) => ({
                    value: plotOption.Name,
                    label: `Plot ${plotOption.Name}`,
                    customOptionFn: (isSelected) => (
                      <div className='flex w-full items-center justify-between gap-3'>
                        <span className='grow'>Plot {plotOption.Name}</span>
                        <span className='plot-competition shrink-0'>
                          <PlotOccupantLabel
                            occupants={(
                              occupantsByPlot[plotOption.Name] ?? []
                            ).filter(
                              (occupant) => occupant.shard_index === shardIndex,
                            )}
                          />
                        </span>
                        <Check
                          size={16}
                          className={`min-w-4 shrink-0 ${
                            isSelected ? 'opacity-100' : 'opacity-0'
                          }`}
                        />
                      </div>
                    ),
                    sortLabel: plotOption.Name.padStart(3, '0'),
                  }))}
                  placeholder='Choose a plot'
                  className='min-w-0'
                />
                <Button
                  variant='outline'
                  size='icon'
                  disabled={!choice || mutationPending}
                  onClick={() => onEditNote(choice ?? null)}
                >
                  <MessageCircleMore className='h-4 w-4' />
                </Button>
              </div>
            )}
          </Card>
        )
      })}
    </div>
  )
}

export default PersonalPlotChoices
